import {
  BusinessUnitQuery,
  OrgMachineDetailsQuery,
  useSelectedVendorQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useSession from '@/buyers/hooks/useSession'
import Action from '@/gf/components/Action'
import CloseModalButton from '@/gf/components/CloseModalButton'
import Modal from '@/gf/components/ModalNext'
import VendorOrderEmail from '@/gf/components/VendorOrderEmail'
import VendorRequestEmail from '@/gf/components/VendorRequestEmail'
import useToggle from '@/gf/hooks/useToggle'
import * as GE from '@/gf/modules/GrammarEvents'
import { ModalSize } from '@/types'
import { DateTime } from 'luxon'
import { useMemo, useState } from 'react'
import { CreateRequestState } from '../types'
import { AddressT } from '@/gf/components/Address'
import { BillingAddressT } from '@/gf/components/BillingAddress'

const PreviewEmailButton = ({
  request,
  orgMachine,
  company,
  billingAddress,
  shippingAddress,
}: {
  request: CreateRequestState
  orgMachine: OrgMachineDetailsQuery['orgMachine']
  company: BusinessUnitQuery['company']
  billingAddress: BillingAddressT | null
  shippingAddress: AddressT
}) => {
  const client = useGqlClient()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [open, toggle] = useToggle()
  const { user, organization } = useSession()

  const { data: vendorData } = useSelectedVendorQuery({
    client,
    variables: {
      id: request.vendors[currentIndex]?.vendorId,
    },
    skip: request.vendors.length === 0,
  })

  const vendor = vendorData?.vendor ?? null
  const vendorContacts = useMemo(() => {
    if (!vendor) {
      return []
    }

    const selectedContacts = request.vendors.find((v) => v.vendorId === vendor.id)?.contactIds ?? []
    return vendor?.contacts.filter((c) => selectedContacts.includes(c.id)) ?? []
  }, [vendor, request.vendors])

  return (
    <>
      <Action.S
        onClick={() => {
          toggle.on()
          GE.whenCreateRequestPath(() =>
            GE.clicksButtonOnFlow('preview-email', GE.UserFlow.CreateRequest)
          )
        }}
      >
        Preview Email
      </Action.S>
      <Modal size={ModalSize.LG} open={open} onClose={toggle.off}>
        <div className="flex flex-col gap-y-3 px-4 py-6">
          <div className="flex justify-end">
            <CloseModalButton onClick={toggle.off} />
          </div>
          <div>
            <h6 className="leading-6 font-medium mb-1">
              {request.quickOrder ? 'Purchase Order' : 'Quote Request'} from {company.name}
            </h6>
            <p className="leading-5 text-gray-600 text-sm">
              From: Gearflow & {'<orders@gearflow.com>'}
              <br />
              To: {vendor?.name} {`<${vendorContacts.map((c) => c.email).join(', ')}>`} <br />
              Reply-To: orders@gearflow.com
            </p>
          </div>

          {request.quickOrder ? (
            <VendorOrderEmail.Wrapper className="max-h-[70vh] overflow-y-auto">
              <VendorOrderEmail
                readonly
                showEmailCopy
                customerName={user.name || company.name}
                customerPhoneNumber={user.phoneNumber || ''}
                customerEmail={user.email}
                vendor={vendor}
                vendorContacts={vendorContacts}
                machineDown={request.urgency?.machineDown ?? null}
                requestTimestamp={DateTime.now()}
                neededBy={request.urgency?.neededByDate ?? null}
                company={company}
                shippingAddress={shippingAddress}
                billingAddress={billingAddress}
                deliveryMethod={request.vendors[currentIndex]?.deliveryMethod ?? null}
                accountNumber={request.vendors[currentIndex]?.accountNumber ?? null}
                machine={
                  orgMachine
                    ? {
                        name: orgMachine.name,
                        serialNumber: orgMachine.serialNumber,
                        make: orgMachine.machine.make,
                        model: orgMachine.machine.model,
                        year: orgMachine.machine.year,
                      }
                    : null
                }
                partRequests={
                  request.parts?.map((p) => ({
                    mpn: p.partNumber ?? '',
                    description: p.description ?? '',
                    quantity: p.quantity,
                  })) ?? []
                }
                additionalDetails={request.comments ?? ''}
                imageUrls={request.parts?.map((p) => p.pictures).flat() ?? []}
                hideUnitNumber={organization.hideUnitNumberFromVendors}
              />
            </VendorOrderEmail.Wrapper>
          ) : (
            <VendorRequestEmail.Wrapper className="max-h-[70vh] overflow-y-auto">
              <VendorRequestEmail
                readonly
                showEmailCopy
                customerName={user.name || company.name}
                customerPhoneNumber={user.phoneNumber || ''}
                customerEmail={user.email}
                vendor={vendor}
                vendorContacts={vendorContacts}
                machineDown={request.urgency?.machineDown ?? null}
                requestTimestamp={DateTime.now()}
                neededBy={request.urgency?.neededByDate ?? null}
                company={company}
                shippingAddress={shippingAddress}
                billingAddress={billingAddress}
                deliveryMethod={request.vendors[currentIndex]?.deliveryMethod ?? null}
                accountNumber={request.vendors[currentIndex]?.accountNumber ?? null}
                machine={
                  orgMachine
                    ? {
                        name: orgMachine.name,
                        serialNumber: orgMachine.serialNumber,
                        make: orgMachine.machine.make,
                        model: orgMachine.machine.model,
                        year: orgMachine.machine.year,
                      }
                    : null
                }
                partRequests={
                  request.parts?.map((p) => ({
                    mpn: p.partNumber ?? '',
                    description: p.description ?? '',
                    quantity: p.quantity,
                  })) ?? []
                }
                additionalDetails={request.comments ?? ''}
                imageUrls={request.parts?.map((p) => p.pictures).flat() ?? []}
                hideUnitNumber={organization.hideUnitNumberFromVendors}
              />
            </VendorRequestEmail.Wrapper>
          )}

          {request.vendors.length > 1 && (
            <div className="flex gap-x-4 justify-end">
              <Action.S
                onClick={() => setCurrentIndex(currentIndex - 1)}
                disabled={currentIndex === 0}
                className="w-24"
              >
                Previous
              </Action.S>
              <Action.S
                onClick={() => setCurrentIndex(currentIndex + 1)}
                disabled={currentIndex === request.vendors.length - 1}
                className="w-24"
              >
                Next
              </Action.S>
            </div>
          )}
        </div>
      </Modal>
    </>
  )
}

export default PreviewEmailButton
